import Modal from 'react-modal';
import { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../Button';

interface EmployeeRow {
    id: string;
    name: string;
    email: string;
    role: string | null;
    clockedIn: boolean;
    since: string | null;
    periodHours: number;
    periodDays: number;
}

interface KioskEmployeesResponse {
    periodStart: string;
    periodEnd: string;
    employees: EmployeeRow[];
}

interface PunchResult {
    success: boolean;
    clockedIn: boolean;
    atUtc: string;
}

const fmtTime = (iso: string) => {
    const d = new Date(iso);
    let h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${h}:${m < 10 ? '0' + m : m} ${ampm}`;
};

const fmtDate = (iso: string) => {
    const d = new Date(iso);
    // Pay period end is exclusive (UTC midnight of the day AFTER the last worked day)
    // — show the last included day so it reads naturally.
    const inclusive = new Date(d.getTime() - 1);
    return inclusive.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
};

const fmtHours = (h: number) => {
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    if (hh === 0 && mm === 0) return '0h';
    if (hh === 0) return `${mm}m`;
    if (mm === 0) return `${hh}h`;
    return `${hh}h ${mm}m`;
};

const initials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join('');

const KioskEmployeePunchModal = ({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}) => {
    const [data, setData] = useState<KioskEmployeesResponse | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [pending, setPending] = useState<EmployeeRow | null>(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<
        (PunchResult & { name: string }) | null
    >(null);
    const [error, setError] = useState<string | null>(null);

    const loadEmployees = async () => {
        setLoadError(null);
        try {
            const { data } = await axios.get<KioskEmployeesResponse>(
                '/api/timeclock/kiosk-employees'
            );
            // Defensive shape check — the 2026-05-13 regression handed
            // back a bare array, the spread defaulted `employees` to
            // undefined, and the modal stuck on "Loading employees…"
            // because no 4xx/5xx fired. Surface this explicitly.
            if (!data || !Array.isArray((data as any).employees)) {
                // eslint-disable-next-line no-console
                console.error(
                    'Failed to load employees: unexpected employees response shape',
                    data
                );
                setLoadError(
                    'Could not load employees. Check time clock API.'
                );
                return;
            }
            setData(data);
        } catch (e: any) {
            // eslint-disable-next-line no-console
            console.error('Failed to load employees', e);
            setLoadError(
                e?.response?.data?.message ||
                    'Could not load employees. Check time clock API.'
            );
        }
    };

    useEffect(() => {
        if (isOpen && data === null) {
            loadEmployees();
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const reset = () => {
        setPending(null);
        setResult(null);
        setError(null);
        setBusy(false);
    };

    const close = () => {
        reset();
        setData(null);
        setIsOpen(false);
    };

    const confirm = async () => {
        if (!pending) return;
        setBusy(true);
        setError(null);
        try {
            const { data: r } = await axios.post<PunchResult>(
                '/api/timeclock/kiosk-punch',
                { email: pending.email }
            );
            setResult({ ...r, name: pending.name });
            setPending(null);
            loadEmployees();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Punch failed.');
        } finally {
            setBusy(false);
        }
    };

    const pickAnother = () => {
        setResult(null);
        setError(null);
    };

    const employees = data?.employees ?? null;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={close}
            ariaHideApp={false}
            className="modal"
        >
            <header className="modal__header">
                <h1 className="modal__header--h1">Employee Clock In / Out</h1>
            </header>

            {result && (
                <>
                    <h2 style={{ textAlign: 'center', marginTop: '1rem' }}>
                        {result.name}
                    </h2>
                    <h1 className="modal__header--h1">
                        <span
                            className={
                                result.clockedIn ? 'green-span' : 'red-span'
                            }
                        >
                            {result.clockedIn
                                ? `Clocked IN at ${fmtTime(result.atUtc)}`
                                : `Clocked OUT at ${fmtTime(result.atUtc)}`}
                        </span>
                    </h1>
                    <Button color={'green'} margin={true} onClick={pickAnother}>
                        Another Punch
                    </Button>
                    <Button color={'red'} margin={true} onClick={close}>
                        Done
                    </Button>
                </>
            )}

            {!result && pending && (
                <>
                    <h2
                        style={{
                            textAlign: 'center',
                            marginTop: '1.5rem',
                            marginBottom: '0.5rem',
                        }}
                    >
                        {pending.clockedIn ? 'Clock OUT' : 'Clock IN'}
                    </h2>
                    <h1
                        style={{
                            textAlign: 'center',
                            margin: '0.25rem 0 1rem',
                            fontSize: '2.25rem',
                        }}
                    >
                        <span
                            className={
                                pending.clockedIn ? 'red-span' : 'green-span'
                            }
                        >
                            {pending.name}
                        </span>
                        ?
                    </h1>
                    {pending.clockedIn && pending.since && (
                        <p
                            style={{
                                textAlign: 'center',
                                color: '#aaa',
                                marginBottom: '0.75rem',
                            }}
                        >
                            Clocked in since {fmtTime(pending.since)}
                        </p>
                    )}

                    <div
                        style={{
                            margin: '0.75rem auto 1.5rem',
                            padding: '1.25rem 1rem',
                            border: '3px solid #22c55e',
                            borderRadius: '0.75rem',
                            maxWidth: '32rem',
                            color: 'inherit',
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '0.75rem',
                                textAlign: 'center',
                                marginBottom: '1rem',
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: '4rem',
                                        fontWeight: 800,
                                        color: '#22c55e',
                                        lineHeight: 1,
                                    }}
                                >
                                    {pending.periodDays}
                                </div>
                                <div
                                    style={{
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1rem',
                                        marginTop: '0.4rem',
                                    }}
                                >
                                    {pending.periodDays === 1
                                        ? 'Day'
                                        : 'Days'}{' '}
                                    this period
                                </div>
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: '4rem',
                                        fontWeight: 800,
                                        color: '#22c55e',
                                        lineHeight: 1,
                                    }}
                                >
                                    {fmtHours(pending.periodHours)}
                                </div>
                                <div
                                    style={{
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1rem',
                                        marginTop: '0.4rem',
                                    }}
                                >
                                    this period
                                </div>
                            </div>
                        </div>
                        {data?.periodEnd && (
                            <div
                                style={{
                                    textAlign: 'center',
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    borderTop: '2px solid #22c55e',
                                    paddingTop: '0.75rem',
                                }}
                            >
                                Pay period ends{' '}
                                <strong style={{ color: '#22c55e' }}>
                                    {fmtDate(data.periodEnd)}
                                </strong>
                            </div>
                        )}
                    </div>

                    {error && (
                        <h3 className="transaction-container__text">
                            <span className="red-span">{error}</span>
                        </h3>
                    )}
                    <Button
                        color={pending.clockedIn ? 'red' : 'green'}
                        margin={true}
                        onClick={confirm}
                    >
                        {busy
                            ? 'Working…'
                            : pending.clockedIn
                            ? `Yes — Clock OUT`
                            : `Yes — Clock IN`}
                    </Button>
                    <Button
                        color={'yellow'}
                        margin={true}
                        onClick={() => setPending(null)}
                    >
                        Cancel
                    </Button>
                </>
            )}

            {!result && !pending && (
                <>
                    {loadError && (
                        <h3 className="transaction-container__text">
                            <span className="red-span">{loadError}</span>
                        </h3>
                    )}
                    {employees === null && !loadError && (
                        <h3 className="transaction-container__text">
                            Loading employees…
                        </h3>
                    )}
                    {employees && employees.length === 0 && (
                        <h3 className="transaction-container__text">
                            No active employees — add some in Payroll Admin.
                        </h3>
                    )}
                    {employees && employees.length > 0 && (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(11rem, 1fr))',
                                gap: '1rem',
                                margin: '1rem 0',
                            }}
                        >
                            {employees.map((e) => (
                                <button
                                    key={e.id}
                                    className="label-block__button"
                                    onClick={() => setPending(e)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '1rem',
                                        minHeight: '9rem',
                                        border: e.clockedIn
                                            ? '3px solid #22c55e'
                                            : '2px solid #555',
                                        background: e.clockedIn
                                            ? 'rgba(34,197,94,0.08)'
                                            : undefined,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '2.5rem',
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            marginBottom: '0.5rem',
                                            color: e.clockedIn
                                                ? '#22c55e'
                                                : '#888',
                                        }}
                                    >
                                        {initials(e.name)}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            textAlign: 'center',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {e.name}
                                    </div>
                                    {e.clockedIn && (
                                        <div
                                            style={{
                                                fontSize: '0.7rem',
                                                color: '#22c55e',
                                                marginTop: '0.35rem',
                                                letterSpacing: '0.1rem',
                                            }}
                                        >
                                            ON THE CLOCK
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                    {data?.periodEnd && employees && employees.length > 0 && (
                        <p
                            style={{
                                textAlign: 'center',
                                marginTop: '0.75rem',
                                fontSize: '1.05rem',
                                fontWeight: 600,
                            }}
                        >
                            Pay period ends{' '}
                            <strong style={{ color: '#22c55e' }}>
                                {fmtDate(data.periodEnd)}
                            </strong>
                        </p>
                    )}
                    <Button color={'red'} margin={true} onClick={close}>
                        Cancel
                    </Button>
                </>
            )}
        </Modal>
    );
};

export default KioskEmployeePunchModal;
